import { Injectable } from '@angular/core';
import { ThemeFamily, themeFamily, ThemeOption, resolveThemeOption } from '@core/theme/domain/domain';
import { ThemeCookieStorageService } from '@core/theme/infrastructure/infrastructure';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';
const THEME_ATTRIBUTE = 'data-theme';
const THEME_FAMILY_ATTRIBUTE = 'data-theme-family';

/** Applies the theme the user already picked in a product app.
 *
 * The pre-paint script in `index.html` does the FIRST stamp — it has to, because it
 * runs before any stylesheet and that is what keeps the page from flashing white.
 * This service exists for what happens after: it re-derives the same result once
 * Angular boots (so the attributes are correct even if that script was skipped) and
 * it follows the OS setting while the page is open, which only a live matchMedia
 * listener can do.
 *
 * There is no setter. The preference belongs to the product apps; see
 * ThemeCookieStorageService.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly _darkMediaQuery = window.matchMedia(DARK_MEDIA_QUERY);
  private _preference: ThemeOption;

  constructor(private _storage: ThemeCookieStorageService) {
    this._preference = resolveThemeOption(this._storage.read());
    this._applyResolvedTheme();
    // Only matters while the preference is `system`: the user flips their OS to dark
    // with the login open and the page has to follow instead of staying light.
    this._darkMediaQuery.addEventListener('change', () => this._onSystemPreferenceChange());
  }

  getPreference(): ThemeOption {
    return this._preference;
  }

  private _onSystemPreferenceChange(): void {
    const followsSystem = this._preference === ThemeOption.system;
    if (!followsSystem) {
      return;
    }
    this._applyResolvedTheme();
  }

  private _applyResolvedTheme(): void {
    const resolved = this._resolve(this._preference);
    const family = themeFamily(resolved);
    this._stamp(resolved, family);
  }

  private _stamp(theme: ThemeOption, family: ThemeFamily): void {
    const root = document.documentElement;
    root.setAttribute(THEME_ATTRIBUTE, theme);
    root.setAttribute(THEME_FAMILY_ATTRIBUTE, family);
    // The pre-paint script stamps color-scheme INLINE, and an inline value outranks the
    // stylesheet rule, so from here on this service owns it: without this the UA would
    // keep painting native controls (scrollbars, autofill, selects) in the boot family.
    root.style.colorScheme = family;
  }

  private _resolve(preference: ThemeOption): ThemeOption {
    if (preference !== ThemeOption.system) {
      return preference;
    }
    return this._darkMediaQuery.matches ? ThemeOption.dark : ThemeOption.light;
  }
}
